import type { NostrEvent } from '@nostrify/nostrify';

export const APP_LISTING_KIND = 38583;

export interface AppListing {
  id: string; // d tag value
  name: string;
  description: string;
  category: string;
  url: string;
  image?: string; // App screenshot/preview image
  logo?: string; // App icon/logo
  author: string; // pubkey (poster)
  creator?: string; // pubkey of actual creator (if different from poster)
  createdAt: number;
  event: NostrEvent;
  isPostedByCreator: boolean; // true if author === creator or no creator tag
}

export const CATEGORIES = [
  'Social',
  'Utility',
  'Gaming',
  'Media',
  'Finance',
  'Development',
  'Productivity',
  'Education',
  'Other',
] as const;

export type AppCategory = typeof CATEGORIES[number];

/**
 * Validates an app listing event according to kind 38583 requirements
 */
export function validateAppEvent(event: NostrEvent): boolean {
  // Check if it's the correct kind
  if (event.kind !== APP_LISTING_KIND) return false;

  // Required tags
  const d = event.tags.find(([name]) => name === 'd')?.[1];
  const name = event.tags.find(([name]) => name === 'name')?.[1];
  const description = event.tags.find(([name]) => name === 'description')?.[1];
  const url = event.tags.find(([name]) => name === 'url')?.[1];
  const category = event.tags.find(([name]) => name === 't')?.[1];

  // All required fields must be present
  if (!d || !name || !description || !url || !category) return false;

  // Category must be valid
  if (!CATEGORIES.includes(category as AppCategory)) return false;

  // URL must be valid
  try {
    new URL(url);
  } catch {
    return false;
  }

  return true;
}

/**
 * Converts a validated Nostr event to an AppListing object
 */
export function eventToAppListing(event: NostrEvent): AppListing | null {
  if (!validateAppEvent(event)) return null;

  const d = event.tags.find(([name]) => name === 'd')?.[1]!;
  const name = event.tags.find(([name]) => name === 'name')?.[1]!;
  const description = event.tags.find(([name]) => name === 'description')?.[1]!;
  const url = event.tags.find(([name]) => name === 'url')?.[1]!;
  const category = event.tags.find(([name]) => name === 't')?.[1]!;
  const image = event.tags.find(([name]) => name === 'image')?.[1];
  const logo = event.tags.find(([name]) => name === 'logo')?.[1];
  const creator = event.tags.find(([name]) => name === 'creator')?.[1];

  const isPostedByCreator = !creator || creator === event.pubkey;

  return {
    id: d,
    name,
    description,
    category,
    url,
    image,
    logo,
    author: event.pubkey,
    creator,
    createdAt: event.created_at,
    event,
    isPostedByCreator,
  };
}

/**
 * Creates tags array for publishing an app listing
 */
export function createAppTags(data: {
  id: string;
  name: string;
  description: string;
  category: string;
  url: string;
  image?: string;
  logo?: string;
  creator?: string;
}): string[][] {
  const tags: string[][] = [
    ['d', data.id],
    ['name', data.name],
    ['description', data.description],
    ['url', data.url],
    ['t', data.category],
    ['alt', `App listing: ${data.name}`],
  ];

  if (data.image) {
    tags.push(['image', data.image]);
  }

  if (data.logo) {
    tags.push(['logo', data.logo]);
  }

  if (data.creator) {
    tags.push(['creator', data.creator]);
  }

  return tags;
}
